import { useEffect } from "react";
import { useSchema } from "../contexts/schema.context";
import { SchemaRegistry } from "src/services/schema/registry/schema.registry";
import { SingleProduct } from "../types/graphql.d";
import { toAbsoluteUrl, getBaseUrl } from "../utils/getBaseUrl";
import { locale } from "src/config/app-config";
import { OfferItemCondition } from "schema-dts";
import { getBrandUrl } from "src/services/schema/utils/helpers";

/**
 * Custom hook for managing product schema generation
 * @param product - Product data from GraphQL
 * @param slug - Product slug for URL generation
 */
export const useProductSchema = (
  product: SingleProduct | undefined,
  slug: string
): void => {
  const { setSchemas, clearSchemas } = useSchema();

  useEffect(() => {
    if (!product) {
      clearSchemas();
      return;
    }

    // Determine availability based on stock status and variations
    const getAvailability = ():
      | "InStock"
      | "OutOfStock"
      | "LimitedAvailability" => {
      if (product.stock_status === "out_of_stock") {
        return "OutOfStock";
      }

      // Check if any variations have stock
      if (product.variations && product.variations.length > 0) {
        const hasStock = product.variations.some(
          (variation) => variation.stock_quantity > 0
        );
        if (!hasStock) {
          return "OutOfStock";
        }

        // Check if stock is limited (less than 10 items total)
        const totalStock = product.variations.reduce(
          (sum, variation) => sum + variation.stock_quantity,
          0
        );
        if (totalStock < 10) {
          return "LimitedAvailability";
        }
      }

      return "InStock";
    };

    // Generate category path for better SEO
    const getCategoryPath = (): string[] => {
      if (
        product.translated_category_names &&
        product.translated_category_names.length > 0
      ) {
        return product.translated_category_names;
      }
      return [];
    };

    const productUrl: string = toAbsoluteUrl(`/product/${slug}`) || "";

    // Generate schemas using the registry with enhanced data
    const schemas = SchemaRegistry.pdp({
      product: {
        name: product.name,
        description: product.description || product.name,
        images: product.images || [],
        sku: product.sku?.split("-").slice(0, -1).join("-") || product.sku,
        brand: product.brand.name,
        brandUrl: getBrandUrl(product.brand.slug, locale),
        price: product.price,
        regularPrice: product.regular_price,
        currency: product.currency?.code || "EUR",
        availability: getAvailability(),
        url: productUrl,

        material: product.material || undefined,
        color: undefined,
        size: undefined,
        rating: undefined,
        gtin: undefined,

        condition: "https://schema.org/NewCondition" as OfferItemCondition,
        category: getCategoryPath().join(" > "),
        careInstructions: product.care_instructions || undefined,
        variations: product.variations?.map((variation) => ({
          sku: variation.id,
          size: variation.size || "",
          availability: variation.stock_quantity > 0 ? "InStock" : "OutOfStock",
          price: product.price,
        })),

        // Product creation and modification dates
        dateCreated: product.date_created?.date,
        dateModified: product.date_modified?.date,

        seller: "Otrium",
        sellerUrl: getBaseUrl(locale),

        // Buy action configuration
        buyActionUrl: productUrl,
      },
    });

    setSchemas(schemas);

    // Cleanup on unmount
    return () => clearSchemas();
  }, [product, slug, locale, setSchemas, clearSchemas]);
};
